import { useState } from "react";
import { CompanyEntity } from "types/Company";

/**
 * What PATCH finvalda-settings takes. The server saves the connection as a
 * whole, so every save repeats what is stored and changes only what the person
 * edited.
 */
export interface FinvaldaSettingsRequest {
  finvaldaAddress: string;
  finvaldaUser: string;
  finvaldaCompanyId: string;
  /** Empty keeps the stored password, but only while address, user and company ID stay as stored. */
  finvaldaPass: string;
  isFinvaldaSyncActive: boolean;
  /** Left out: unchanged. Empty: cleared. */
  finvaldaSalesJournalGroup?: string;
}

/** The request that saves exactly what is stored now. A company without Finvalda has null fields. */
export function storedFinvaldaRequest(
  company: CompanyEntity
): FinvaldaSettingsRequest {
  return {
    finvaldaAddress: (company.finvaldaAddress ?? "").trim(),
    finvaldaUser: company.finvaldaUser ?? "",
    finvaldaCompanyId: (company.finvaldaCompanyId ?? "").trim(),
    finvaldaPass: "",
    isFinvaldaSyncActive: Boolean(company.isFinvaldaSyncActive),
  };
}

/** Address, user, company ID and password are all saved, so the other Finvalda settings can be saved too. */
export function isFinvaldaConnectionSaved(company: CompanyEntity): boolean {
  const stored = storedFinvaldaRequest(company);
  return Boolean(
    stored.finvaldaAddress &&
      stored.finvaldaUser &&
      stored.finvaldaCompanyId &&
      company.finvaldaPass
  );
}

/** The same format the server accepts for the sales journal group (ASCII only, so Finvalda URLs stay valid). */
export const JOURNAL_GROUP_FORMAT = /^[A-Za-z0-9 ._$-]{1,40}$/;

// What a refused save says, by the code the server answers with. Any other
// failure gets the generic line.
const SAVE_ERROR_TEXT_KEYS: Record<string, string> = {
  FINVALDA_PASSWORD_REQUIRED: "finvalda_password_required",
  FINVALDA_ADDRESS_NOT_ALLOWED: "settings_finvalda_address_not_allowed",
  FINVALDA_JOURNAL_GROUP_INVALID: "settings_finvalda_journal_group_invalid",
};

export function finvaldaSaveErrorKey(err: unknown): string {
  // The server answers with a bare string or a { code } / { error } object.
  const body = (err as { response?: { data?: any } })?.response?.data;
  const code = body?.code || body?.error || body;
  return SAVE_ERROR_TEXT_KEYS[code] ?? "finvalda_save_failed";
}

/**
 * Every Finvalda save sends the whole connection, so two saves at once could
 * overwrite each other. The page lets one run at a time: while `isLocked`, its
 * switch and Save buttons are disabled.
 */
export interface SaveLock {
  isLocked: boolean;
  /** Runs `save` and keeps the lock until it ends. */
  hold: <T>(save: () => Promise<T>) => Promise<T>;
}

export function useSaveLock(): SaveLock {
  const [isLocked, setIsLocked] = useState(false);

  const hold = async <T>(save: () => Promise<T>) => {
    setIsLocked(true);
    try {
      return await save();
    } finally {
      setIsLocked(false);
    }
  };

  return { isLocked, hold };
}
